import React, { useMemo } from 'react';
import { Box, Container, Grid, Typography, Paper } from '@mui/material';
import { Verified as CheckIcon, School as CapIcon } from '@mui/icons-material';
import { Reveal, HOME_COLORS, sectionLabelSx } from './common';
import { COLLEGE_NAME } from '../../branding';

export default function AboutSection({ profile, posts }) {
  const description = profile?.description
    || `${COLLEGE_NAME} is committed to shaping compassionate, skilled nursing professionals through rigorous academics, hands-on clinical exposure and values-based education.`;

  const heroPhoto = useMemo(() => {
    for (const post of posts || []) {
      const first = (post.photos || [])[0];
      if (first) return `/api${first.url}`;
    }
    return null;
  }, [posts]);

  return (
    <Box id="about" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff', scrollMarginTop: '80px' }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={5}>
            <Reveal>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{
                  borderRadius: 5, overflow: 'hidden', aspectRatio: '4/5', bgcolor: HOME_COLORS.paper,
                  boxShadow: '0 24px 48px rgba(0,77,64,0.18)',
                }}>
                  {heroPhoto ? (
                    <Box component="img" src={heroPhoto} alt="Campus life" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <Box sx={{
                      width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${HOME_COLORS.dark}, ${HOME_COLORS.sky})`,
                    }}>
                      <CapIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.85)' }} />
                    </Box>
                  )}
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute', bottom: -24, right: { xs: 12, md: -24 }, p: 2.2, borderRadius: 4,
                    display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: 220,
                    boxShadow: '0 16px 32px rgba(0,77,64,0.2)', animation: 'floatY 5s ease-in-out infinite',
                  }}
                >
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `linear-gradient(135deg, ${HOME_COLORS.sky}, ${HOME_COLORS.skyLight})`,
                  }}>
                    <CheckIcon sx={{ color: '#fff', fontSize: 22 }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: HOME_COLORS.dark, lineHeight: 1.3 }}>
                    Council Recognized Programs
                  </Typography>
                </Paper>
              </Box>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={7} sx={{ pl: { md: 4 } }}>
            <Reveal delay={100}>
              <Typography sx={sectionLabelSx}>About Us</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: HOME_COLORS.dark, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
                Building the next generation of nursing professionals
              </Typography>
              <Typography sx={{ color: '#4a5a58', fontSize: '1.05rem', lineHeight: 1.8, mb: 2 }}>
                {description}
              </Typography>
              {profile?.affiliation && (
                <Typography sx={{ color: '#4a5a58', fontSize: '1rem', lineHeight: 1.8 }}>
                  <strong>Affiliation:</strong> {profile.affiliation}
                </Typography>
              )}
              {profile?.principalName && (
                <Typography sx={{ color: '#4a5a58', fontSize: '1rem', lineHeight: 1.8 }}>
                  <strong>Principal:</strong> {profile.principalName}
                </Typography>
              )}
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
